// PATH: src/pages/admin/tabs/StudiesTab.jsx
// VERSION: 1.2 — Versículos en Textarea (coma / salto de línea) + preview tags

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Heading,
  Spinner,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiRefreshCw,
  FiSave,
  FiPlusCircle,
  FiEdit2,
  FiTrash2,
} from 'react-icons/fi';
import axiosInstance from '../../../api/axiosConfig';



export default function StudiesTab() {
  const toast = useToast();

  const headingColor = useColorModeValue('brand.800', 'text.default');
  const subTextColor = useColorModeValue('brand.500', 'text.muted');
  const tagBorder = useColorModeValue('brand.300', 'border.default');
  const tagBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const theadBg = useColorModeValue('brand.100', 'whiteAlpha.200');
  const rowHoverBg = useColorModeValue('brand.50', 'whiteAlpha.100');

  const bw = {
    box: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.200', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'xl',
      boxShadow: 'sm',
    },
    input: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'md',
      _placeholder: { color: useColorModeValue('brand.400', 'text.muted'), fontWeight: 'semibold' },
      _hover: { borderColor: useColorModeValue('brand.400', 'brand.300') },
      _focus: {
        borderColor: 'accent.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)',
      },
    },
    btn: {
      borderRadius: 'md',
      bg: 'accent.500',
      border: '1px solid',
      borderColor: 'accent.500',
      color: 'white',
      fontWeight: 'semibold',
      _hover: {
        bg: 'accent.600',
        borderColor: 'accent.600',
        transform: 'translateY(-1px)',
        boxShadow: 'md',
      },
      _active: {
        transform: 'translateY(0)',
        boxShadow: 'sm',
      },
    },
    btnGhost: {
      borderRadius: 'md',
      bg: useColorModeValue('white', 'whiteAlpha.100'),
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      color: useColorModeValue('brand.700', 'text.default'),
      fontWeight: 'semibold',
      _hover: {
        bg: useColorModeValue('brand.50', 'whiteAlpha.200'),
        borderColor: useColorModeValue('brand.400', 'brand.300'),
      },
    },
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [form, setForm] = useState({
    id: null,
    tema: '',
    resumen: '',
    versiculos: '',
    video: '',
    reflexion: '',
    serie: '',
  });

  async function load() {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        '/studies?limit=1000&offset=0&order=newest&q=' +
        encodeURIComponent(q),
      );
      setItems(data?.items || []);
    } catch {
      toast({ status: 'error', title: 'Error cargando estudios' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [q]);

  // Convierte string en lista, aceptando coma, punto y coma y saltos de línea
  function toList(s) {
    return Array.from(
      new Set(
        (s || '')
          .split(/[,;\n]/)
          .map((t) => t.trim())
          .filter(Boolean),
      ),
    );
  }

  const previewVerses = useMemo(
    () => toList(form.versiculos),
    [form.versiculos],
  );

  function editRow(r) {
    setForm({
      id: r.id,
      tema: r.tema || '',
      resumen: r.resumen || '',
      versiculos: (r.versiculos || []).join(', '),
      video: r.video || '',
      reflexion: r.reflexion || '',
      serie: r.serie || '',
    });
  }

  function resetForm() {
    setForm({
      id: null,
      tema: '',
      resumen: '',
      versiculos: '',
      video: '',
      reflexion: '',
      serie: '',
    });
  }

  async function save() {
    const payload = {
      tema: form.tema,
      resumen: form.resumen,
      versiculos: toList(form.versiculos), // ← array de strings
      video: form.video || null,
      reflexion: form.reflexion || null,
      serie: form.serie || null,
    };
    if (!payload.tema || !payload.resumen) {
      toast({ status: 'warning', title: 'Tema y resumen son requeridos' });
      return;
    }
    try {
      if (form.id) {
        await axiosInstance.put(`/studies/${form.id}`, payload);
        toast({ status: 'success', title: 'Estudio actualizado' });
      } else {
        await axiosInstance.post('/studies', payload);
        toast({ status: 'success', title: 'Estudio creado' });
      }
      resetForm();
      load();
    } catch (e) {
      console.error(e);
      toast({ status: 'error', title: 'No se pudo guardar' });
    }
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar estudio?')) return;
    try {
      await axiosInstance.delete(`/studies/${id}`);
      toast({ status: 'success', title: 'Eliminado' });
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo eliminar' });
    }
  }

  return (
    <Box {...bw.box} p={4}>
      <Heading fontSize="lg" mb={1} color={headingColor}>
        Estudios
      </Heading>
      <Box fontSize="sm" mb={4} color={subTextColor}>
        Guarda estudios bíblicos con resumen, versículos base, reflexión y video opcional.
      </Box>

      {/* Buscador */}
      <Flex gap={2} mb={3} wrap="wrap">
        <Input
          {...bw.input}
          placeholder={useBreakpointValue({
            base: 'Buscar…',
            md: 'Buscar por tema, resumen o versículos…',
          })}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Button
          {...bw.btnGhost}
          leftIcon={<FiRefreshCw />}
          onClick={load}
        >
          Refrescar
        </Button>
      </Flex>

      {/* Formulario */}
      <Flex gap={2} mb={3} wrap="wrap">
        <Input
          {...bw.input}
          placeholder="Tema"
          value={form.tema}
          onChange={(e) =>
            setForm((f) => ({ ...f, tema: e.target.value }))
          }
        />
        <Input
          {...bw.input}
          placeholder="Serie (opcional)"
          value={form.serie}
          onChange={(e) =>
            setForm((f) => ({ ...f, serie: e.target.value }))
          }
        />
      </Flex>
      <Flex gap={2} mb={3} wrap="wrap">
        <Input
          {...bw.input}
          placeholder="Video (URL opcional)"
          value={form.video}
          onChange={(e) =>
            setForm((f) => ({ ...f, video: e.target.value }))
          }
        />
      </Flex>

      <Textarea
        {...bw.input}
        placeholder="Resumen"
        rows={3}
        mb={2}
        value={form.resumen}
        onChange={(e) =>
          setForm((f) => ({ ...f, resumen: e.target.value }))
        }
      />

      <Textarea
        {...bw.input}
        placeholder="Reflexión (opcional)"
        rows={3}
        mb={2}
        value={form.reflexion}
        onChange={(e) =>
          setForm((f) => ({ ...f, reflexion: e.target.value }))
        }
      />

      {/* Versículos: cuadro para lista separada por comas / saltos de línea */}
      <Textarea
        {...bw.input}
        placeholder="Versículos base (separados por coma o salto de línea, ej: Juan 3:16, Romanos 8:28)"
        rows={2}
        mb={2}
        value={form.versiculos}
        onChange={(e) =>
          setForm((f) => ({ ...f, versiculos: e.target.value }))
        }
      />

      {/* Preview de versículos como tags */}
      <Wrap mb={3}>
        {previewVerses.map((v, i) => (
          <WrapItem key={i}>
            <Tag border="1px solid" borderColor={tagBorder} bg={tagBg}>
              <TagLabel>{v}</TagLabel>
            </Tag>
          </WrapItem>
        ))}
      </Wrap>

      <Flex gap={2} mb={4} wrap="wrap">
        <Button
          {...bw.btn}
          leftIcon={form.id ? <FiSave /> : <FiPlusCircle />}
          onClick={save}
        >
          {form.id ? 'Actualizar' : 'Crear'}
        </Button>
        {form.id && (
          <Button {...bw.btnGhost} onClick={resetForm}>
            Cancelar
          </Button>
        )}
      </Flex>

      {/* Tabla */}
      {loading ? (
        <Flex justify="center" align="center" py={8}>
          <Spinner thickness="3px" speed="0.65s" />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table
            size="sm"
            variant="simple"
            sx={{ 'th, td, tr, thead, tbody': { borderColor: 'brand.200' } }}
            border="1px solid"
            borderColor="brand.200"
          >
            <Thead bg={theadBg}>
              <Tr>
                <Th borderColor="brand.200">ID</Th>
                <Th borderColor="brand.200">Tema</Th>
                <Th borderColor="brand.200">Serie</Th>
                <Th borderColor="brand.200">Resumen</Th>
                <Th borderColor="brand.200">Versículos</Th>
                <Th borderColor="brand.200">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((r) => (
                <Tr key={r.id} _hover={{ bg: rowHoverBg }}>
                  <Td borderColor="brand.200">{r.id}</Td>
                  <Td borderColor="brand.200">{r.tema}</Td>
                  <Td borderColor="brand.200">{r.serie || '-'}</Td>
                  <Td borderColor="brand.200">{r.resumen}</Td>
                  <Td borderColor="brand.200">
                    {(r.versiculos || []).join(', ')}
                  </Td>
                  <Td borderColor="brand.200" whiteSpace="nowrap">
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      mr={2}
                      leftIcon={<FiEdit2 />}
                      onClick={() => editRow(r)}
                    >
                      Editar
                    </Button>
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      leftIcon={<FiTrash2 />}
                      onClick={() => remove(r.id)}
                    >
                      Eliminar
                    </Button>
                  </Td>
                </Tr>
              ))}
              {items.length === 0 && (
                <Tr>
                  <Td
                    colSpan={5}
                    textAlign="center"
                    py={4}
                    borderColor="brand.200"
                  >
                    No hay estudios registrados.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
